import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { DollarSign, ArrowRight, Sparkles } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";

/**
 * Dashboard banner pushing paid plans to free users.
 *
 * Visibility:
 *   - Hidden if user has Season Pass (nothing to upsell)
 *   - Shows "Upgrade to Season Pass" variant if they have ≥1 single-tournament sub
 *   - Otherwise the "Enter a prize pool" pitch
 */
export function PrizePoolBanner() {
  const { hasSeasonPass, activeCount } = useSubscription();

  if (hasSeasonPass) return null;

  const isUpgrade = activeCount > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent p-4 md:p-5 mb-6"
    >
      {/* Decorative glow */}
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-primary/20 blur-3xl pointer-events-none" />

      <div className="relative flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
        <div className="w-11 h-11 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
          {isUpgrade ? <Sparkles className="w-6 h-6 text-primary" /> : <DollarSign className="w-6 h-6 text-primary" />}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">
            {isUpgrade ? "✨ Unlock Every Tournament" : "🏆 Playing for free"}
          </p>
          <h3 className="font-display text-lg md:text-xl font-bold text-foreground leading-tight">
            {isUpgrade
              ? <>Win prizes <span className="text-primary">year-round</span> with the Season Pass</>
              : <>Enter a prize pool and win <span className="text-primary">real prizes</span></>
            }
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
            {isUpgrade
              ? "Upgrade to Season Pass for $19.99 more — unlock every tournament this season."
              : "From $9.99/mo per tournament, or unlock everything with Season Pass for $29.99/mo."
            }
          </p>
        </div>

        <div className="flex gap-2 shrink-0">
          <Link to={isUpgrade ? "/checkout-success?type=single_tournament&offer=upgrade" : "/pricing"}>
            <button className="h-10 px-4 rounded-xl bg-primary text-primary-foreground font-display text-sm font-bold hover:bg-primary/90 transition-colors inline-flex items-center gap-1.5">
              {isUpgrade ? "Upgrade for $19.99" : "See Plans"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
